// src/utils/currencyUtils.js
// TCMB'den güncel döviz kurlarını çeken yardımcı fonksiyonlar

/**
 * TCMB'den güncel döviz kurlarını çeker
 * @returns {Promise<Object>} Döviz kurları objesi { USD: number, EUR: number, GBP: number }
 */
export const fetchTCMBRates = async () => {
    try {
        // TCMB günlük kur XML endpoint'i
        // CORS sorunu nedeniyle proxy kullanıyoruz
        const proxyUrl = 'https://api.allorigins.win/raw?url=';
        const tcmbUrl = 'https://www.tcmb.gov.tr/kurlar/today.xml';

        const response = await fetch(proxyUrl + encodeURIComponent(tcmbUrl));

        if (!response.ok) {
            throw new Error('TCMB verisi alınamadı');
        }

        const xmlText = await response.text();

        // XML'i parse et
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlText, 'text/xml');

        // Döviz kurlarını al
        const currencies = xmlDoc.getElementsByTagName('Currency');
        const rates = {};

        for (let i = 0; i < currencies.length; i++) {
            const currency = currencies[i];
            const code = currency.getAttribute('CurrencyCode');
            const forexSelling = currency.getElementsByTagName('ForexSelling')[0];

            if (forexSelling && forexSelling.textContent) {
                // TCMB ondalık ayracı olarak virgül kullanıyor
                rates[code] = parseFloat(forexSelling.textContent.replace(',', '.'));
            }
        }

        return {
            USD: rates['USD'] || null,
            EUR: rates['EUR'] || null,
            GBP: rates['GBP'] || null,
            date: new Date().toLocaleDateString('tr-TR'),
            success: true
        };
    } catch (error) {
        console.error('TCMB kur çekme hatası:', error);

        // Hata durumunda alternatif API dene
        try {
            return await fetchAlternativeRates();
        } catch (altError) {
            console.error('Alternatif API hatası:', altError);
            return {
                USD: null,
                EUR: null,
                GBP: null,
                success: false,
                error: 'Döviz kurları şu anda alınamıyor. Lütfen manuel girin.'
            };
        }
    }
};

/**
 * Alternatif API'den döviz kurlarını çeker (TCMB erişilemezse)
 */
const fetchAlternativeRates = async () => {
    // ExchangeRate-API kullan (ücretsiz, sınırlı)
    const response = await fetch('https://api.exchangerate-api.com/v4/latest/TRY');

    if (!response.ok) {
        throw new Error('Alternatif API erişilemedi');
    }

    const data = await response.json();

    // Bu API TRY bazlı veriyor, tersini almamız gerekiyor
    return {
        USD: data.rates.USD ? (1 / data.rates.USD) : null,
        EUR: data.rates.EUR ? (1 / data.rates.EUR) : null,
        GBP: data.rates.GBP ? (1 / data.rates.GBP) : null,
        date: new Date().toLocaleDateString('tr-TR'),
        success: true,
        source: 'alternative'
    };
};

/**
 * Belirli bir döviz için kuru çeker
 * @param {string} currencyCode - Döviz kodu (USD, EUR, GBP)
 * @returns {Promise<number|null>} Döviz kuru veya null
 */
export const fetchSingleRate = async (currencyCode) => {
    if (currencyCode === 'TRY') return 1;

    const rates = await fetchTCMBRates();
    return rates[currencyCode] || null;
};

/**
 * Kurları cache'le (localStorage)
 */
export const getCachedRates = () => {
    try {
        const cached = localStorage.getItem('tcmb_rates');
        if (!cached) return null;

        const data = JSON.parse(cached);
        const today = new Date().toLocaleDateString('tr-TR');

        // Aynı gün içindeki cache'i kullan
        if (data.date === today) {
            return data;
        }
        return null;
    } catch {
        return null;
    }
};

export const setCachedRates = (rates) => {
    try {
        localStorage.setItem('tcmb_rates', JSON.stringify(rates));
    } catch (error) {
        console.error('Cache kaydetme hatası:', error);
    }
};

/**
 * Önce cache'ten, yoksa API'den kurları al
 */
export const getExchangeRates = async () => {
    // Önce cache'e bak
    const cached = getCachedRates();
    if (cached && cached.success) {
        return cached;
    }

    // API'den çek
    const rates = await fetchTCMBRates();

    // Başarılıysa cache'le
    if (rates.success) {
        setCachedRates(rates);
    }

    return rates;
};

const currencyUtils = {
    fetchTCMBRates,
    fetchSingleRate,
    getExchangeRates,
    getCachedRates,
    setCachedRates
};

export default currencyUtils;
